/**
 * Texts of the Vendas tab (ANLT-04): the app × site ticket comparison and the
 * share of orders from returning customers.
 */
import type { TenantSalesMetricsResponseDto } from '@/generated/korza-api-client/types.gen';

export interface TicketComparison {
  /** `|app ÷ site − 1|`, 0–1. */
  ratio: number;
  direction: 'higher' | 'lower';
  /** Site ticket of the period: mean of the buckets' `aovWeb` above 0. */
  siteTicket: number;
}

/**
 * App ticket against the site's (ANLT-04 AC 2); `null` when either is 0
 * (no text).
 */
export function compareTicketToSite(
  appTicket: number,
  aovWebPerBucket: number[]
): TicketComparison | null {
  const withSales = aovWebPerBucket.filter((value) => value > 0);
  if (appTicket <= 0 || withSales.length === 0) return null;
  const siteTicket =
    withSales.reduce((sum, value) => sum + value, 0) / withSales.length;
  const diff = appTicket / siteTicket - 1;
  return {
    ratio: Math.abs(diff),
    direction: diff >= 0 ? 'higher' : 'lower',
    siteTicket,
  };
}

/** In-app repurchases + channel migrations of the period. */
export function repurchaseCount(
  metrics: TenantSalesMetricsResponseDto
): number {
  const { inAppRepurchases, channelMigrationRepurchases } =
    metrics.ordersComposition;
  return inAppRepurchases.count + channelMigrationRepurchases.count;
}

/**
 * Share of the period's orders placed by customers who had bought before
 * (ANLT-04, Recompras); `null` without orders.
 */
export function repurchaseShare(
  metrics: TenantSalesMetricsResponseDto
): number | null {
  return metrics.totalPaidOrders > 0
    ? repurchaseCount(metrics) / metrics.totalPaidOrders
    : null;
}
